/**
 * Utilitaire de validation de mot de passe
 * Vérifie que le mot de passe respecte les critères de sécurité
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validatePassword = (password: string): PasswordValidationResult => {
  const result: PasswordValidationResult = {
    isValid: true,
    errors: []
  };

  // Vérifier la longueur minimale
  if (password.length < 8) {
    result.isValid = false;
    result.errors.push('Le mot de passe doit contenir au moins 8 caractères');
  }

  // Vérifier la présence d'au moins une lettre majuscule
  if (!/[A-Z]/.test(password)) {
    result.isValid = false;
    result.errors.push('Le mot de passe doit contenir au moins une lettre majuscule');
  }

  // Vérifier la présence d'au moins une lettre minuscule
  if (!/[a-z]/.test(password)) {
    result.isValid = false;
    result.errors.push('Le mot de passe doit contenir au moins une lettre minuscule');
  }

  // Vérifier la présence d'au moins un chiffre
  if (!/[0-9]/.test(password)) {
    result.isValid = false;
    result.errors.push('Le mot de passe doit contenir au moins un chiffre');
  }

  // Vérifier la présence d'au moins un caractère spécial
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    result.isValid = false;
    result.errors.push('Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*(),.?":{}|<>)');
  }

  return result;
};

// Fonction pour calculer la force du mot de passe (0-100)
export const calculatePasswordStrength = (password: string): number => {
  if (!password) return 0;
  
  let strength = 0;
  
  // Longueur de base
  strength += Math.min(password.length * 2, 20); // Max 20 points pour la longueur
  
  // Complexité
  if (/[A-Z]/.test(password)) strength += 10; // Majuscules
  if (/[a-z]/.test(password)) strength += 10; // Minuscules
  if (/[0-9]/.test(password)) strength += 10; // Chiffres
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 20; // Caractères spéciaux
  
  // Variété de caractères
  const uniqueChars = new Set(password.split('')).size;
  strength += Math.min(uniqueChars * 2, 20); // Max 20 points pour la variété
  
  // Bonus pour la combinaison de différents types
  let typesCount = 0;
  if (/[A-Z]/.test(password)) typesCount++;
  if (/[a-z]/.test(password)) typesCount++;
  if (/[0-9]/.test(password)) typesCount++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) typesCount++;
  
  if (typesCount >= 3) strength += 10;
  if (typesCount === 4) strength += 10;
  
  return Math.min(strength, 100);
};

// Fonction pour obtenir un texte descriptif de la force du mot de passe
export const getPasswordStrengthText = (strength: number): string => {
  if (strength < 20) return 'Très faible';
  if (strength < 40) return 'Faible';
  if (strength < 60) return 'Moyen';
  if (strength < 80) return 'Fort';
  return 'Très fort';
};

// Fonction pour obtenir une couleur représentant la force du mot de passe
export const getPasswordStrengthColor = (strength: number): string => {
  if (strength < 20) return '#ff0000'; // Rouge
  if (strength < 40) return '#ff8000'; // Orange
  if (strength < 60) return '#ffff00'; // Jaune
  if (strength < 80) return '#80ff00'; // Vert clair
  return '#00ff00'; // Vert
};
